/** composable */
import { type AxiosError } from "axios";

interface RequestResult<T> {
   status: "fulfilled" | "rejected";
   value?: T;
   reason?: AxiosError | unknown;
}

export function useApiRequest() {
   const isLoading = ref(true);
   const loadErrors: Ref<Array<Error>> = ref([]);

   /**
    * Executes multiple API requests in parallel.
    * @param requests An array where each element is a function that returns a promise.
    * @returns Array of results of the promises (includes failed promises)
    */
   async function execManyRequests<T extends any[]>(requests: { [K in keyof T]: () => Promise<T[K]> }): Promise<{
      [K in keyof T]: RequestResult<T[K]>;
   }> {
      isLoading.value = true;
      loadErrors.value = [];

      const results = await Promise.allSettled(requests.map((fn) => fn()));
      isLoading.value = false;

      // Add failed promises to the loadErrors array
      results.forEach((result, index) => {
         if (result.status === "rejected") {
            loadErrors.value.push(result.reason as Error);
         }
      });

      return results as { [K in keyof T]: RequestResult<T[K]> };
   }

   /** Executes a single API request.
    * @param request A promise (any type)
    * @returns Result from the promise
    */
   async function execRequest<T>(requestFn: () => Promise<T>): Promise<T | undefined> {
      isLoading.value = true;
      loadErrors.value = [];

      try {
         const result = await requestFn();
         return result;
      } catch (error) {
         loadErrors.value.push(error as Error);
      } finally {
         isLoading.value = false;
      }
   }

   return { isLoading, loadErrors, execRequest, execManyRequests };
}
